import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, PlusCircle, MessageCircle, User, LogOut, Home, Heart, Shield } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function Navbar() {
  const { isAuthed, user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    // Only poll once auth state is resolved AND the user is logged in — avoids
    // pre-auth 401 noise in the console for guests.
    if (loading || !isAuthed) { setUnread(0); return; }
    const load = () => api.get("/conversations/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [loading, isAuthed, location.pathname]);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/zoeken?q=${encodeURIComponent(q)}`);
  };

  const accountMenu = isAuthed ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="navbar-profile-menu" aria-label="Account" className="flex items-center rounded-full border border-[#333] p-1 transition-colors hover:border-[#FF6A00]">
          <img src={user.avatar} alt="" className="h-9 w-9 rounded-full bg-[#2A2A2A]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 border-[#333] bg-[#1c1c1c] text-base text-[#F5F5F5]">
        <div className="px-3 py-2 text-sm text-[#B4B4B4]">Ingelogd als <b className="text-[#F5F5F5]">{user.name}</b></div>
        <DropdownMenuSeparator className="bg-[#333]" />
        <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/profiel")} className="cursor-pointer py-2.5 text-[15px] focus:bg-[#2A2A2A]"><User className="mr-2 h-4 w-4" /> Mijn profiel</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-chats" onClick={() => navigate("/chats")} className="cursor-pointer py-2.5 text-[15px] focus:bg-[#2A2A2A]"><MessageCircle className="mr-2 h-4 w-4" /> Chats {unread > 0 && <span className="ml-auto rounded-full bg-[#FF6A00] px-1.5 text-xs font-bold text-[#111]">{unread}</span>}</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-favorites" onClick={() => navigate("/profiel?tab=favorieten")} className="cursor-pointer py-2.5 text-[15px] focus:bg-[#2A2A2A]"><Heart className="mr-2 h-4 w-4" /> Favorieten</DropdownMenuItem>
        {user.role === "admin" && (
          <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="cursor-pointer py-2.5 text-[15px] text-[#FFB27A] focus:bg-[#2A2A2A]"><Shield className="mr-2 h-4 w-4" /> Admin dashboard</DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="bg-[#333]" />
        <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate("/"); }} className="cursor-pointer py-2.5 text-[15px] text-[#FF6A00] focus:bg-[#2A2A2A]"><LogOut className="mr-2 h-4 w-4" /> Uitloggen</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : (
    <Link to="/inloggen" data-testid="navbar-login-btn" className="rounded-full border border-[#333] px-5 py-2.5 text-[15px] font-semibold text-[#F5F5F5] transition-colors hover:border-[#FF6A00] hover:text-[#FF6A00]">Inloggen</Link>
  );

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-[#333] bg-[#111]/85 backdrop-blur-xl">
        {/* Desktop bar */}
        <div className="mx-auto hidden max-w-7xl items-center gap-6 px-8 py-3 md:flex">
          <Logo height={52} className="shrink-0" />
          <form onSubmit={submitSearch} className="relative flex-1" data-testid="navbar-search-form">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#B4B4B4]" />
            <input
              data-testid="navbar-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Zoek naar iets moois..."
              className="w-full rounded-full border border-[#333] bg-[#2A2A2A] py-3 pl-12 pr-4 text-[16px] text-[#F5F5F5] outline-none transition-colors placeholder:text-[#8A8A8A] focus:border-[#FF6A00]"
            />
          </form>
          <div className="flex items-center gap-3">
            <Link to="/plaatsen" data-testid="navbar-place-ad-btn" className="flex items-center gap-2 rounded-full bg-[#FF6A00] px-5 py-2.5 text-[15px] font-bold text-[#111] transition-all duration-200 hover:bg-[#FFB27A] active:scale-95">
              <PlusCircle className="h-5 w-5" /> Plaatsen
            </Link>
            {isAuthed && (
              <Link to="/chats" data-testid="navbar-chats-btn" className="relative rounded-full border border-[#333] p-2.5 text-[#F5F5F5] transition-colors hover:border-[#FF6A00] hover:text-[#FF6A00]" aria-label="Chats">
                <MessageCircle className="h-5 w-5" />
                {unread > 0 && <span data-testid="navbar-unread-badge" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#FF6A00] px-1 text-[11px] font-bold text-[#111]">{unread}</span>}
              </Link>
            )}
            {accountMenu}
          </div>
        </div>

        {/* Mobile header — big centered logo on its own row, then search + compact account */}
        <div className="md:hidden">
          <div className="flex justify-center px-4 pt-4">
            <Logo widthClass="w-[82vw] max-w-[340px]" className="justify-center" />
          </div>
          <div className="flex items-center gap-2 px-4 pb-3 pt-3">
            <form onSubmit={submitSearch} className="relative flex-1" data-testid="navbar-search-form-mobile">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#B4B4B4]" />
              <input
                data-testid="navbar-search-input-mobile"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Zoek naar iets moois..."
                className="w-full rounded-full border border-[#333] bg-[#2A2A2A] py-3 pl-12 pr-4 text-[16px] text-[#F5F5F5] outline-none placeholder:text-[#8A8A8A] focus:border-[#FF6A00]"
              />
            </form>
            {isAuthed ? accountMenu : (
              <Link to="/inloggen" data-testid="navbar-login-btn-mobile" aria-label="Inloggen" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#333] text-[#F5F5F5] transition-colors hover:border-[#FF6A00] hover:text-[#FF6A00]">
                <User className="h-5 w-5" />
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-[#333] bg-[#111]/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl md:hidden">
        <div className="flex items-center justify-around px-2 py-2">
          <BottomLink to="/" icon={Home} label="Home" active={location.pathname === "/"} testid="bottom-home" />
          <BottomLink to="/zoeken" icon={Search} label="Zoeken" active={location.pathname === "/zoeken"} testid="bottom-search" />
          <Link to="/plaatsen" data-testid="bottom-place" aria-label="Plaats advertentie" className="-mt-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#FF6A00] text-[#111] shadow-lg shadow-[#FF6A00]/30 transition-transform active:scale-95">
            <PlusCircle className="h-7 w-7" />
          </Link>
          <BottomLink to="/chats" icon={MessageCircle} label="Chats" active={location.pathname.startsWith("/chats")} testid="bottom-chats" badge={unread} />
          <BottomLink to="/profiel" icon={User} label="Profiel" active={location.pathname === "/profiel"} testid="bottom-profile" />
        </div>
      </nav>
    </>
  );
}

function BottomLink({ to, icon: Icon, label, active, testid, badge = 0 }) {
  return (
    <Link to={to} data-testid={testid} className={`relative flex flex-col items-center gap-0.5 px-3 py-1 text-[13px] font-medium ${active ? "text-[#FF6A00]" : "text-[#B4B4B4]"}`}>
      <Icon className="h-6 w-6" strokeWidth={active ? 2.5 : 2} />
      {label}
      {badge > 0 && <span className="absolute right-1 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF6A00] px-1 text-[10px] font-bold text-[#111]">{badge}</span>}
    </Link>
  );
}
